import { Navigate, Outlet } from "react-router";

import { useUser } from "@/contexts/UserContext";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useScopeParams } from "@/hooks/useScopeParams";

type FeatureFlagBlockProps = {
  /** Flag(s) whose presence makes the route unreachable. Any one blocks. */
  componentFlag: string | string[];
  /** Rendered when no blocking flag is granted. Defaults to `<Outlet />`. */
  children?: React.ReactNode;
  /**
   * When true (the default) a blocked route redirects to the scope's 404.
   * Pass false only where rendering nothing is deliberately correct.
   */
  redirectToNotFound?: boolean;
};

/**
 * Route-level guard for *blocking* flags — the inverse of `FeatureFlagGuard`.
 *
 * Entitlement flags are positive: the route renders only when granted. A few
 * flags run the other way, describing a surface a tenant should not reach
 * because their feed leaves it empty; that cannot be expressed as a missing
 * entitlement, since the same flag has to leave the screen intact for everyone
 * else. Removing the links in is not enough on its own — a bookmark, a shared
 * URL or the back stack still gets there — so the route carries the flag too.
 */
export default function FeatureFlagBlock({
  componentFlag,
  children,
  redirectToNotFound = true,
}: FeatureFlagBlockProps) {
  const { userInfo } = useUser();
  const { buildUrl } = useScopeParams();
  const isBlocked = useFeatureFlag(componentFlag);

  // Grants arrive with userInfo. Until it resolves we cannot tell "not blocked"
  // from "not loaded yet" — and here the safe default is to render nothing
  // rather than flash a screen that is about to be taken away.
  if (!userInfo) return null;

  if (!isBlocked) return <>{children ?? <Outlet />}</>;

  if (redirectToNotFound) return <Navigate to={buildUrl("404")} replace />;

  return null;
}
